import {
  Box,
  Chip,
  Fab,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import {
  blue, indigo, lightBlue,
} from '@mui/material/colors';
import {
  useEffect, useState, useMemo, useCallback,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OptimusInput from '../../../../common/library/Input/Input';
import OptimusButton from '../../../../common/library/Button/Button';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  selectGetCompanyPartnerRelationsState,
  selectGetCompanyPartnerRelations,
  selectCategories,
  selectPartnersByCategories,
  selectGetPartnersByCategoriesState,
  selectAllActivePartners,
  selectGetAllActivePartnersState,
} from '../../../../store/slices/dashboardSlice';
import { PartnerCard } from '../../../../common/PartnerCard';
import { OuterContainer } from '../../DashboardOuterContainerPagesLayout';
import { PartnerCardSkeleton } from '../../../../common/PartnerEnrollmentCardSkeleton';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useIsMobile } from '../../../../common/hooks/useResponsive';
import { usePartnerConfigList } from './usePartnerConfigList';
import { TestIds } from '../../../../assets/testIds';
import { useTrackedChecklistUpdate } from '../../Home/useTrackedChecklistUpdate';
import { PartnerEnrollmentProvider } from './usePartnerEnrollmentWithExtraStep';
import {
  getCompanyPartnerRelations,
  getAllCategories,
  getPartnersByCategories,
  getRecentRebatePartners,
  getAllActivePartners,
} from '../../../../store/thunks/dashboardThunk';
import { PartnerCategoryFilter } from './PartnerCategoryFilter';
import { PartnerCategory, PartnerEnrollmentStatus } from '../../../../common/enums';
import { PartnersHeader } from './PartnersHeader';
import { getPartnerEnrollmentStatus } from '../../../../common/utils/PartnerEnrollmentUtils';
import { withoutDuplicatePartners } from '../../../../common/utils/DuplicatePartners';
import {
  PARTNERS_ROUTE_HOME_DEPOT,
  PARTNERS_ROUTE_SUNBELT_RENTALS,
  PARTNERS_ROUTE_WINSUPPLY,
} from '../../../../routes/namedRoutes';

export function Partners() {
  const getCompanyPartnerRelationsState = useAppSelector(selectGetCompanyPartnerRelationsState);
  const companyPartnerRelations = useAppSelector(selectGetCompanyPartnerRelations);
  const categories = useAppSelector(selectCategories);
  const partnersByCategories = useAppSelector(selectPartnersByCategories);
  const getPartnersByCategoriesState = useAppSelector(selectGetPartnersByCategoriesState);
  const allActivePartners = useAppSelector(selectAllActivePartners);
  const getAllActivePartnersState = useAppSelector(selectGetAllActivePartnersState);
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] =
    useState<PartnerCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Tab selection - 0: All Partners, 1: Your Partners
  // Initialize from URL query parameter if present
  const initialTab = searchParams.get('tab') === 'your-partners' ? 1 : 0;
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const memberInfo = useAppSelector(selectMemberInformation);
  const { company } = memberInfo;

  const subscriptionTier = company?.subscription?.subscriptionTier;
  // Partner access is cumulative: every active partner is requested and each one is then
  // gated on the member's own rung by `usePartnerConfigList`, which renders the ones above
  // it locked with an Upgrade or Contact Sales call to action. Asking the endpoint for a
  // single tier cannot express that - `membershipType=edge` hid every paid partner from an
  // Edge member, and `membershipType=srt` hid every Edge partner from a paying one (SN-996).

  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (company?.id) {
      dispatch(getCompanyPartnerRelations(company.id));
      dispatch(getAllActivePartners());
    }
    if (memberInfo?.id) {
      dispatch(getRecentRebatePartners({ memberId: memberInfo.id }));
    }
  }, [company?.id, memberInfo?.id, dispatch]);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    // Only fetch filtered partners when categories are selected
    if (selectedCategories.length > 0) {
      const categoryIds =
        categories?.filter((cat) => selectedCategories.includes(cat.key)).map((cat) => cat.id) ||
        [];

      dispatch(getPartnersByCategories({ categoryIds }));
    }
  }, [categories, dispatch, selectedCategories]);

  // Updating checklist step1 as completed if user lands on this page first
  useTrackedChecklistUpdate(t('checklist.steps.step1.key'));

  // Filter partners by enrollment status for "Your partners" tab
  const enrolledPartners = useMemo(() => {
    if (!allActivePartners || !companyPartnerRelations) {
      return [];
    }
    return allActivePartners.filter((partner) => {
      const relation = companyPartnerRelations.find(
        (rel) => rel.partnerId === partner.id,
      );
      const enrollmentStatus = getPartnerEnrollmentStatus(relation);
      return enrollmentStatus === PartnerEnrollmentStatus.ActionRequired ||
        enrollmentStatus === PartnerEnrollmentStatus.InProgress ||
        enrollmentStatus === PartnerEnrollmentStatus.Completed;
    });
  }, [allActivePartners, companyPartnerRelations]);

  const partnerLinkSx = {
    color: theme.palette.brand.primary,
    textDecoration: 'underline',
    fontSize: 'inherit',
    verticalAlign: 'baseline',
  };

  // Use partners filtered by categories when available,
  // otherwise use all active partners or enrolled partners based on tab
  let partnersToDisplay;
  let isLoadingPartners;

  if (selectedTab === 1) {
    // Your Partners tab
    if (selectedCategories.length > 0) {
      // Filter enrolled partners by selected categories
      partnersToDisplay = enrolledPartners.filter((partner) => (
        partnersByCategories?.some((p) => p.id === partner.id)
      ));
      isLoadingPartners = (
        getCompanyPartnerRelationsState === 'pending' ||
        getPartnersByCategoriesState === 'pending' ||
        getAllActivePartnersState === 'pending'
      );
    } else {
      partnersToDisplay = enrolledPartners;
      isLoadingPartners = (
        getCompanyPartnerRelationsState === 'pending' ||
        getAllActivePartnersState === 'pending'
      );
    }
  } else if (selectedCategories.length > 0) {
    // All Partners tab with category filter
    partnersToDisplay = withoutDuplicatePartners(partnersByCategories, subscriptionTier);
    isLoadingPartners = getPartnersByCategoriesState === 'pending';
  } else {
    // All Partners tab without filter
    partnersToDisplay = withoutDuplicatePartners(allActivePartners, subscriptionTier);
    isLoadingPartners = getAllActivePartnersState === 'pending';
  }

  const partnerConfigList = usePartnerConfigList();

  // Get category label from the API data or fallback to enum
  const getCategoryLabel = (category: PartnerCategory): string => {
    const categoryData = categories?.find((cat) => cat.key === category);
    return categoryData?.displayName || category;
  };

  // Remove a category from selected categories
  const handleRemoveCategory = (categoryToRemove: PartnerCategory) => {
    setSelectedCategories(
      selectedCategories.filter((cat) => cat !== categoryToRemove),
    );
  };

  // Get partner IDs from filtered API results
  const filteredPartnerIds = new Set(partnersToDisplay?.map((p) => p.id) || []);

  // Filter partner config list based on API results and sort by priority
  const filteredPartnerConfigList = partnerConfigList
    .filter((partner) => {
      // For "All Partners" tab with no category filters, show all partners
      if (selectedTab === 0 && selectedCategories.length === 0) {
        return true;
      }
      // Locked partners carry SRT IDs that never appear in the edge-tier category response.
      // Match them against their own category metadata instead.
      if (partner.isLocked && selectedCategories.length > 0 && selectedTab === 0) {
        return partner.categories.some((cat) => selectedCategories.includes(cat));
      }
      // Otherwise (Your Partners tab OR categories selected), filter by API results
      return filteredPartnerIds.has(partner.id);
    })
    .sort((a, b) => {
      // Locked (SRT-only) partners always sort after all available (free) partners.
      // Within each of those two groups, popular/new/tier badges take priority,
      // same as before. Dedup between free and locked is already handled upstream
      // in usePartnerConfigList's merge, so no dup-checking is needed here - the one
      // exception is a duplicated partner the member is enrolled with, whose Edge copy
      // that merge deliberately keeps so the enrollment stays reachable (SN-996).
      const getGroupPriority = (partner: typeof a) => {
        if (partner.isLocked) {
          // Priority 4: popular + locked
          if (partner.isPopularPartner) return 4;
          // Priority 5: locked
          return 5;
        }
        // Priority 1: Popular badge partners (free tier)
        if (partner.isPopularPartner) return 1;
        // Priority 2: New badge partners (free tier)
        if (partner.isNewPartner) return 2;
        // Partners with "low" tier go to the bottom of the available (free) group
        if (partner.tier === 'low') return 3.5;
        // Priority 3: Available (free) partners
        return 3;
      };

      const priorityDiff = getGroupPriority(a) - getGroupPriority(b);
      if (priorityDiff !== 0) return priorityDiff;

      // Alphabetical within each priority group
      return a.title.localeCompare(b.title);
    });

  // Apply search filter on top of category filter (it includes all partners)
  const searchFilteredPartners = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return filteredPartnerConfigList;

    return filteredPartnerConfigList.filter(
      (partner) => partner.title.toLowerCase().includes(query) ||
        partner.description?.toLowerCase().includes(query),
    );
  }, [searchQuery, filteredPartnerConfigList]);

  // Calculate the count for "Your Partners" tab badge
  // Use the enrolled partners count (filter config list by enrolled partner IDs)
  const enrolledPartnerIds = new Set(enrolledPartners.map((p) => p.id));
  const yourPartnersCount = partnerConfigList.filter(
    (partner) => enrolledPartnerIds.has(partner.id),
  ).length;

  return (
    <PartnerEnrollmentProvider>
      <OuterContainer
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          padding: theme.spacing(0),
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
          [theme.breakpoints.down('md')]: {
            background: 'none',
            border: 'none',
          },
        }}
      >
        <>
          <PartnersHeader />
          <Box
            sx={{
              width: '100%',
              padding: theme.spacing(0),
            }}
          >
            {
              // Only show skeleton on initial load for partner config list
              (getCompanyPartnerRelationsState === 'pending' && partnerConfigList.length === 0) ?
                <Box
                  sx={{
                    mt: theme.spacing(18),
                  }}
                >
                  <PartnerCardSkeleton numberOfSkeletons={6} />
                </Box> :
                <Box>
                  <Box
                    sx={{
                      position: 'relative',
                      mb: theme.spacing(3),
                      borderBottom: isMobile ? 'none' : `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Tabs
                      value={selectedTab}
                      onChange={(_, newValue) => setSelectedTab(newValue)}
                      sx={{
                        '& .MuiTabs-indicator': {
                          backgroundColor: theme.palette.brand.primary,
                        },
                      }}
                    >
                      <Tab
                        label={t('partners.allPartners')}
                        sx={{
                          mt: theme.spacing(1),
                          fontSize: '16px',
                          fontWeight: 500,
                          color: theme.palette.grey[600],
                          '&.Mui-selected': {
                            color: theme.palette.brand.primary,
                          },
                        }}
                        data-testid={TestIds.partnersPage.allPartnersTab}
                      />
                      <Tab
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                            {t('partners.yourPartners')}
                            <Chip
                              label={yourPartnersCount}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '12px',
                                backgroundColor: selectedTab === 1 ?
                                  theme.palette.brand.primary :
                                  theme.palette.grey[300],
                                color: selectedTab === 1 ?
                                  'white' :
                                  theme.palette.grey[700],
                              }}
                            />
                          </Box>
                        }
                        sx={{
                          mt: theme.spacing(1),
                          fontSize: '16px',
                          fontWeight: 500,
                          color: theme.palette.grey[600],
                          '&.Mui-selected': {
                            color: theme.palette.brand.primary,
                          },
                        }}
                        data-testid={TestIds.partnersPage.yourPartnersTab}
                      />
                    </Tabs>
                    {/* Search and Filter positioned on top-right */}
                    <Box
                      sx={{
                        position: isMobile ? 'static' : 'absolute',
                        right: 0,
                        top: 0,
                        display: 'flex',
                        gap: theme.spacing(2),
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        width: isMobile ? '100%' : 'auto',
                        mt: isMobile ? theme.spacing(2) : 0,
                      }}
                    >
                      <OptimusInput
                        placeholderText={t('partners.searchPartners') || 'Search'}
                        value={searchQuery}
                        onInputChange={(e) => setSearchQuery(e.target.value)}
                        customVariant="iconLeading"
                        leadingIcon={<SearchIcon />}
                        trailingIcon={searchQuery ? (
                          <CloseIcon
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setSearchQuery('')}
                          />
                        ) : undefined}
                        width={isMobile ? '100%' : theme.spacing(40)}
                        sx={{
                          minWidth: 'unset',
                        }}
                        textFieldSx={{
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.grey[300],
                            borderWidth: theme.spacing(0.125),
                          },
                        }}
                        data-testid={TestIds.partnersPage.searchInput}
                      />
                      <PartnerCategoryFilter
                        selectedCategories={selectedCategories}
                        onCategoriesChange={setSelectedCategories}
                        availableCategories={categories
                          ?.filter((cat) => cat.partnerCount > 0)
                          .map((cat) => cat.key)
                        }
                      />
                    </Box>
                  </Box>
                  {selectedCategories.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1),
                        mb: theme.spacing(3),
                        justifyContent: 'flex-end',
                      }}
                    >
                      {selectedCategories.map((category) => (
                        <Chip
                          key={category}
                          label={getCategoryLabel(category)}
                          onDelete={() => handleRemoveCategory(category)}
                          sx={{
                            alignItems: 'center',
                            gap: theme.spacing(0.5),
                            border: `1px solid ${lightBlue[100]}`,
                            backgroundColor: indigo[50],
                            height: 'auto',
                            '& .MuiChip-label': {
                              paddingRight: theme.spacing(0),
                              paddingLeft: theme.spacing(1.25),
                              color: theme.palette.brand.primary,
                              fontSize: theme.spacing(1.75),
                              fontWeight: 500,
                            },
                            '& .MuiChip-deleteIcon': {
                              paddingRight: theme.spacing(0.5),
                              color: theme.palette.brand.primary,
                              fontSize: theme.spacing(2.25),
                              margin: 0,
                              '&:hover': {
                                color: blue[900],
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {isLoadingPartners && (
                    <PartnerCardSkeleton numberOfSkeletons={selectedTab === 1 ? 3 : 6} />
                  )}
                  {!isLoadingPartners && searchFilteredPartners.length > 0 && (
                    <Grid container spacing={2.5}>
                      {searchFilteredPartners
                        .map((partner) => (
                          <Grid key={partner.id} item sm={12} md={6} lg={4} >
                            <PartnerCard
                              PartnerLogo={partner.logo}
                              partnerInfo={partner}
                              showEnrollmentStatus={selectedTab === 1}
                            />
                          </Grid>
                        ))
                      }
                    </Grid>
                  )}
                  {!isLoadingPartners && searchFilteredPartners.length === 0 && (
                    selectedTab === 1 && !searchQuery.trim() && selectedCategories.length === 0 ? (
                      <Box
                        data-testid={TestIds.partnersPage.yourPartnersEmptyState}
                        sx={{
                          backgroundColor: theme.palette.common.white,
                          borderRadius: '12px',
                          boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.04), 0px 0px 4px 0px rgba(0, 0, 0, 0.02), 0px 0px 1px 1px rgba(0, 0, 0, 0.04)',
                          p: '16px 20px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: theme.spacing(1.5),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '18px',
                            fontWeight: 590,
                            lineHeight: '24px',
                            color: theme.palette.text.primary,
                            textAlign: 'center',
                          }}
                        >
                          {t('partners.yourPartnersEmptyTitle')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: theme.palette.text.primary,
                          }}
                        >
                          <Trans
                            i18nKey="partners.yourPartnersEmptyBody"
                            t={t}
                            components={{
                              hdLink: (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <Link
                                  component="button"
                                  onClick={() => navigate(PARTNERS_ROUTE_HOME_DEPOT, { state: { fromTab: 'your-partners' } })}
                                  sx={partnerLinkSx}
                                />
                              ),
                              sunbeltLink: (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <Link
                                  component="button"
                                  onClick={() => navigate(PARTNERS_ROUTE_SUNBELT_RENTALS, { state: { fromTab: 'your-partners' } })}
                                  sx={partnerLinkSx}
                                />
                              ),
                              winsupplyLink: (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <Link
                                  component="button"
                                  onClick={() => navigate(PARTNERS_ROUTE_WINSUPPLY, { state: { fromTab: 'your-partners' } })}
                                  sx={partnerLinkSx}
                                />
                              ),
                            }}
                          />
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <OptimusButton
                            data-testid={TestIds.partnersPage.yourPartnersEmptyCta}
                            customVariant="primary"
                            size="m"
                            buttonText={t('partners.yourPartnersEmptyCta') || ''}
                            onClick={() => setSelectedTab(0)}
                            fullWidth={isMobile}
                            width={isMobile ? undefined : '306px'}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: theme.spacing(30),
                          p: theme.spacing(4),
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          data-testid={TestIds.partnersPage.noResultsMessage}
                        >
                          {t('partners.noResults')}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
            }
          </Box>
          {showBackToTop && (
            <Fab
              size="small"
              onClick={scrollToTop}
              sx={{
                position: 'fixed',
                bottom: theme.spacing(4),
                right: theme.spacing(4),
                zIndex: 1000,
              }}
            >
              <KeyboardArrowUpIcon />
            </Fab>
          )}
        </>
      </OuterContainer>
    </PartnerEnrollmentProvider>
  );
}
